
import React, { createContext, useContext, useState, useEffect, FC, ReactNode } from 'react';
import { Product, CartItem } from '@/types/product';
import { useFavorites } from '@/hooks/useFavorites';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface CartContextType {
  cart: CartItem[];
  addToCart: (product: Product, quantity: number) => void;
  removeFromCart: (productId: string) => void;
  updateCartItemQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  getTotalPoints: () => number;
  getItemsCount: () => number;
}

const CartContext = createContext<CartContextType>({
  cart: [],
  addToCart: () => {},
  removeFromCart: () => {},
  updateCartItemQuantity: () => {},
  clearCart: () => {},
  getTotalPoints: () => 0,
  getItemsCount: () => 0,
});

export const useCart = (): CartContextType => useContext(CartContext);

export const CartProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const { toggleFavorite, isFavorite } = useFavorites();
  const { user } = useAuth();

  // Get user-specific cart key
  const getCartKey = () => {
    return user ? `cart_${user.id}` : 'cart_guest';
  };

  // Load cart from localStorage when user changes
  useEffect(() => {
    const cartKey = getCartKey();

    // Migrate old cart data to user-specific storage (one-time migration)
    if (user && !localStorage.getItem(cartKey)) {
      const oldCart = localStorage.getItem('cart');
      if (oldCart) {
        try {
          const parsedOldCart = JSON.parse(oldCart);
          localStorage.setItem(cartKey, oldCart);
          localStorage.removeItem('cart'); // Clean up old cart
          setCart(parsedOldCart);
          return;
        } catch (e) {
          console.error('Failed to migrate old cart:', e);
        }
      }
    }

    // Load user-specific cart
    const storedCart = localStorage.getItem(cartKey);
    if (storedCart) {
      try {
        setCart(JSON.parse(storedCart));
      } catch (e) {
        console.error('Failed to parse cart from localStorage:', e);
        setCart([]);
      }
    } else {
      // Clear cart if no stored data for this user
      setCart([]);
    }
  }, [user?.id]); // Re-run when user changes

  // Update localStorage whenever cart changes
  useEffect(() => {
    const cartKey = getCartKey();
    localStorage.setItem(cartKey, JSON.stringify(cart));
  }, [cart, user?.id]);

  const addToCart = (product: Product, quantity: number) => {
    setCart((prevCart) => {
      const existingItemIndex = prevCart.findIndex(item => item.productId === product.id);

      if (existingItemIndex >= 0) {
        // Se o produto já estiver no carrinho, atualizar quantidade
        const updatedCart = [...prevCart];
        updatedCart[existingItemIndex].quantity += quantity;

        // Remover dos favoritos se estiver lá (only if user is logged in)
        if (user && isFavorite(product.id)) {
          toggleFavorite(product.id);
        }

        toast.success(`${quantity} unidade(s) adicionada(s) ao carrinho!`);
        return updatedCart;
      } else {
        // Se for um novo produto, adicionar ao carrinho
        // Remover dos favoritos se estiver lá (only if user is logged in)
        if (user && isFavorite(product.id)) {
          toggleFavorite(product.id);
        }

        toast.success(`${product.name} adicionado ao carrinho!`);
        return [...prevCart, {
          productId: product.id,
          product,
          quantity
        }];
      }
    });
  };

  const removeFromCart = (productId: string) => {
    setCart(prevCart => prevCart.filter(item => item.productId !== productId));
  };

  const updateCartItemQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setCart(prevCart => 
      prevCart.map(item => 
        item.productId === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    // Also clear from localStorage for current user
    const cartKey = getCartKey();
    localStorage.removeItem(cartKey);
  };

  const getTotalPoints = () => {
    return cart.reduce((total, item) => total + (item.product.pointsPrice * item.quantity), 0);
  };

  const getItemsCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{ 
      cart, 
      addToCart, 
      removeFromCart, 
      updateCartItemQuantity,
      clearCart,
      getTotalPoints,
      getItemsCount
    }}>
      {children}
    </CartContext.Provider>
  );
};
